import React, { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Settings, Image, Plus, Trash2, ChevronDown, ChevronUp, ArrowLeft, X } from 'lucide-react';
import { useOrgaConfigurationContext } from '@/app/providers';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { StorageService } from '@/services/firebase/storage-service';
import {
  getOrganizationLogoErrorMessage,
  getOrganizationLogoUrl,
  ORGANIZATION_LOGO_ACCEPT,
  validateOrganizationLogoFile,
} from '@/shared/utils/organization-logo';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { usePersonalizationContext } from '@/shared/contexts/PersonalizationContext';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import './PersonalizationEditor.css';

// Helper pour vérifier si un contenu HTML est effectivement vide
const isEmptyHtml = (html: string | undefined | null) => {
  if (!html) return true;
  // Supprimer les balises HTML et les entités espace pour voir s'il reste du texte
  const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  // Quill peut retourner <p><br></p> ou <p></p>
  return stripped.length === 0;
};

interface PersonalizationEditorProps {
  organizationId: string;
}

// Helper component pour gérer les couleurs et gradients
const ColorOrGradientInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  defaultColor: string;
  label: string;
  theme: any;
  forceMode?: 'solid' | 'gradient'; // Force un mode spécifique (masque le sélecteur)
}> = ({ value, onChange, defaultColor, label, theme, forceMode }) => {
  const [useGradient, setUseGradient] = React.useState(false);
  const [gradientColor1, setGradientColor1] = React.useState('#9333EA');
  const [gradientColor2, setGradientColor2] = React.useState('#EC4899');
  const [gradientDirection, setGradientDirection] = React.useState('right');

  React.useEffect(() => {
    // Si forceMode est défini, l'utiliser
    let isGradient = false;
    if (forceMode === 'gradient') {
      isGradient = true;
      setUseGradient(true);
    } else if (forceMode === 'solid') {
      isGradient = false;
      setUseGradient(false);
    } else {
      isGradient = value?.includes('gradient') || false;
      setUseGradient(isGradient);
    }

    if (isGradient && value) {
      // Parser le gradient de manière plus simple
      const directionMatch = value.match(/to\s+([^,]+),/);
      const colorsMatch = value.match(/#[0-9A-Fa-f]{3,6}/g);
      if (directionMatch) {
        setGradientDirection(directionMatch[1].trim());
      }
      if (colorsMatch && colorsMatch.length >= 2) {
        setGradientColor1(colorsMatch[0]);
        setGradientColor2(colorsMatch[1]);
      }
    } else if (value && !isGradient) {
      setGradientColor1(value);
    }
  }, [value, forceMode]);

  const updateGradient = (color1: string, color2: string, direction: string) => {
    onChange(`linear-gradient(to ${direction}, ${color1}, ${color2})`);
  };

  return (
    <div className="space-y-2">
      {!forceMode && (
        <select
          value={useGradient ? 'gradient' : 'solid'}
          onChange={(e) => {
            const newUseGradient = e.target.value === 'gradient';
            setUseGradient(newUseGradient);
            if (newUseGradient) {
              updateGradient(gradientColor1, gradientColor2, gradientDirection);
            } else {
              onChange(gradientColor1);
            }
          }}
          className="w-full px-3 py-2 border border-border rounded-md text-sm"
          style={{
            color: theme.colors.foreground || '#111827',
            backgroundColor: theme.colors.background || '#FFFFFF',
            borderColor: theme.colors.border || '#D1D5DB',
          }}
        >
          <option value="solid">Couleur unie</option>
          <option value="gradient">Gradient</option>
        </select>
      )}
      {useGradient ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="color"
              value={gradientColor1}
              onChange={(e) => {
                const newColor = e.target.value;
                setGradientColor1(newColor);
                updateGradient(newColor, gradientColor2, gradientDirection);
              }}
              className="personalization-color-picker"
              aria-label="Couleur 1"
            />
            <input
              type="text"
              value={gradientColor1}
              onChange={(e) => {
                const newColor = e.target.value;
                setGradientColor1(newColor);
                updateGradient(newColor, gradientColor2, gradientDirection);
              }}
              placeholder="#9333EA"
              className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
              style={{
                color: theme.colors.foreground || '#111827',
                backgroundColor: theme.colors.background || '#FFFFFF',
                borderColor: theme.colors.border || '#D1D5DB',
              }}
            />
          </div>
          <div className="flex gap-2">
            <input
              type="color"
              value={gradientColor2}
              onChange={(e) => {
                const newColor = e.target.value;
                setGradientColor2(newColor);
                updateGradient(gradientColor1, newColor, gradientDirection);
              }}
              className="personalization-color-picker"
              aria-label="Couleur 2"
            />
            <input
              type="text"
              value={gradientColor2}
              onChange={(e) => {
                const newColor = e.target.value;
                setGradientColor2(newColor);
                updateGradient(gradientColor1, newColor, gradientDirection);
              }}
              placeholder="#EC4899"
              className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
              style={{
                color: theme.colors.foreground || '#111827',
                backgroundColor: theme.colors.background || '#FFFFFF',
                borderColor: theme.colors.border || '#D1D5DB',
              }}
            />
          </div>
          <select
            value={gradientDirection}
            onChange={(e) => {
              const newDirection = e.target.value;
              setGradientDirection(newDirection);
              updateGradient(gradientColor1, gradientColor2, newDirection);
            }}
            className="w-full px-3 py-2 border border-border rounded-md text-sm"
            style={{
              color: theme.colors.foreground || '#111827',
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#D1D5DB',
            }}
          >
            <option value="right">→ Droite</option>
            <option value="left">← Gauche</option>
            <option value="bottom">↓ Bas</option>
            <option value="top">↑ Haut</option>
            <option value="bottom right">↘ Bas-Droite</option>
            <option value="top right">↗ Haut-Droite</option>
            <option value="bottom left">↙ Bas-Gauche</option>
            <option value="top left">↖ Haut-Gauche</option>
          </select>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="color"
            value={value && !value.includes('gradient') ? value : gradientColor1}
            onChange={(e) => onChange(e.target.value)}
            className="personalization-color-picker"
            aria-label={label}
          />
          <input
            type="text"
            value={value && !value.includes('gradient') ? value : gradientColor1}
            onChange={(e) => onChange(e.target.value)}
            placeholder={defaultColor}
            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
            style={{
              color: theme.colors.foreground || '#111827',
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#D1D5DB',
            }}
          />
        </div>
      )}
    </div>
  );
};

// Composant séparé pour éviter les re-renders excessifs avec useWatch
const CustomFieldsList: React.FC<{ form: any; theme: any }> = ({ form, theme }) => {
  const customFields = useWatch({
    control: form.control,
    name: 'personalization.productModelConfig.customFields',
    defaultValue: []
  }) || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="block text-xs font-medium" style={{ color: theme.colors.foreground || '#111827' }}>Champs personnalisés</div>
        <button
          type="button"
          onClick={() => {
            const currentFields = form.getValues('personalization.productModelConfig.customFields') || [];
            const newField = {
              key: `field_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
              label: '',
              placeholder: '',
              type: 'text' as const,
              required: false,
            };
            form.setValue('personalization.productModelConfig.customFields', [
              ...currentFields,
              newField,
            ]);
          }}
          className="px-3 py-1.5 text-xs bg-primary-10 text-primary rounded hover:bg-primary-20 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Ajouter
        </button>
      </div>

      {customFields.map((field: any, index: number) => {
        const fieldKey = field.key || `field-${index}`;

        return (
          <div
            key={fieldKey}
            className="p-3 border rounded space-y-2"
            style={{
              borderColor: theme.colors.border || '#D1D5DB',
              backgroundColor: theme.colors.background || '#FFFFFF',
            }}
          >
            <div className="flex items-center justify-between">
              <h5 className="text-xs font-medium" style={{ color: theme.colors.foreground || '#111827' }}>Champ {index + 1}</h5>
              <button
                type="button"
                onClick={() => {
                  const currentFields = form.getValues('personalization.productModelConfig.customFields') || [];
                  form.setValue('personalization.productModelConfig.customFields', currentFields.filter((_: any, i: number) => i !== index));
                }}
                className="p-1 rounded transition-colors"
                style={{
                  color: '#DC2626',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FEF2F2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div>
              <label htmlFor={`custom-field-label-${fieldKey}`} className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Label *</label>
              <input
                id={`custom-field-label-${fieldKey}`}
                type="text"
                {...form.register(`personalization.productModelConfig.customFields.${index}.label` as const, {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    // Générer automatiquement la clé à partir du label uniquement si la clé est vide ou générique
                    const label = e.target.value;
                    const currentKey = form.getValues(`personalization.productModelConfig.customFields.${index}.key`);
                    if (label && (!currentKey || currentKey.startsWith('field_'))) {
                      const key = label.toLowerCase()
                        .trim()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_+|_+$/g, '') || `field_${Date.now()}`;
                      form.setValue(`personalization.productModelConfig.customFields.${index}.key` as any, key);
                    }
                  }
                })}
                className="w-full min-w-0 px-2 py-1.5 border rounded text-xs"
                style={{
                  borderColor: theme.colors.border || '#D1D5DB',
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  color: theme.colors.foreground || '#111827',
                }}
                placeholder="ex: Type, Taille, Couleur"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label htmlFor={`custom-field-placeholder-${fieldKey}`} className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Placeholder</label>
                <input
                  id={`custom-field-placeholder-${fieldKey}`}
                  type="text"
                  {...form.register(`personalization.productModelConfig.customFields.${index}.placeholder` as const)}
                  className="w-full min-w-0 px-2 py-1.5 border rounded text-xs"
                  style={{
                    borderColor: theme.colors.border || '#D1D5DB',
                    backgroundColor: theme.colors.background || '#FFFFFF',
                    color: theme.colors.foreground || '#111827',
                  }}
                  placeholder="ex: Saisissez..."
                />
              </div>
              <div>
                <label htmlFor={`custom-field-type-${fieldKey}`} className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Type</label>
                <select
                  id={`custom-field-type-${fieldKey}`}
                  {...form.register(`personalization.productModelConfig.customFields.${index}.type` as const)}
                  className="w-full min-w-0 px-2 py-1.5 border rounded text-xs"
                  style={{
                    borderColor: theme.colors.border || '#D1D5DB',
                    backgroundColor: theme.colors.background || '#FFFFFF',
                    color: theme.colors.foreground || '#111827',
                  }}
                >
                  <option value="text">Texte</option>
                  <option value="textarea">Zone de texte</option>
                  <option value="number">Nombre</option>
                  <option value="email">Email</option>
                  <option value="tel">Téléphone</option>
                  <option value="url">URL</option>
                  <option value="date">Date</option>
                </select>
              </div>
            </div>
            {form.watch(`personalization.productModelConfig.customFields.${index}.type`) === 'date' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <div>
                  <label htmlFor={`custom-field-mindate-${fieldKey}`} className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Date de début</label>
                  <input
                    id={`custom-field-mindate-${fieldKey}`}
                    type="date"
                    {...form.register(`personalization.productModelConfig.customFields.${index}.minDate` as const)}
                    className="w-full min-w-0 px-2 py-1.5 border rounded text-xs"
                    style={{
                      borderColor: theme.colors.border || '#D1D5DB',
                      backgroundColor: theme.colors.background || '#FFFFFF',
                      color: theme.colors.foreground || '#111827',
                    }}
                  />
                </div>
                <div>
                  <label htmlFor={`custom-field-maxdate-${fieldKey}`} className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Date de fin</label>
                  <input
                    id={`custom-field-maxdate-${fieldKey}`}
                    type="date"
                    {...form.register(`personalization.productModelConfig.customFields.${index}.maxDate` as const)}
                    className="w-full min-w-0 px-2 py-1.5 border rounded text-xs"
                    style={{
                      borderColor: theme.colors.border || '#D1D5DB',
                      backgroundColor: theme.colors.background || '#FFFFFF',
                      color: theme.colors.foreground || '#111827',
                    }}
                  />
                </div>
              </div>
            )}
            <div className="flex items-center">
              <input
                id={`custom-field-required-${fieldKey}`}
                type="checkbox"
                {...form.register(`personalization.productModelConfig.customFields.${index}.required` as const)}
                className="mr-2"
                style={{
                  accentColor: theme.colors.primary || '#9333EA',
                }}
              />
              <label htmlFor={`custom-field-required-${fieldKey}`} className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>Champ requis</label>
            </div>
          </div>
        );
      })}

      {customFields.length === 0 && (
        <div
          className="p-3 text-center text-xs border rounded"
          style={{
            color: theme.colors.foregroundSecondary || '#6B7280',
            borderColor: theme.colors.border || '#D1D5DB',
            backgroundColor: theme.colors.backgroundTertiary || '#F3F4F6',
          }}
        >
          Aucun champ personnalisé. Cliquez sur "Ajouter" pour en créer un.
        </div>
      )}
    </div>
  );
};

const PersonalizationEditor: React.FC<PersonalizationEditorProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const { configuration } = useOrgaConfigurationContext();
  const { theme } = useTheme();
  const { setTemporaryPersonalization } = usePersonalizationContext();
  const navigate = useNavigate();
  const { idorga } = useParams<{ idorga: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSaving, setIsSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    general: true,
    productModel: false,
    theme: true,
    layoutBackground: false,
    cards: false,
    inputs: false,
    buttons: false,
    texts: false,
    badges: false,
    icons: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const form = useForm<any>({
    defaultValues: {
      name: '',
      personalization: {
        logo: '',
        description: '',
        informativeAnnouncement: '',
        productModelConfig: {
          format: 'custom',
          modelPlaceholder: '',
          typePlaceholder: '',
          sizePlaceholder: '',
          customFields: [
            {
              key: 'model',
              label: 'Model',
              placeholder: 'Ex: Nom du produit, Référence...',
              type: 'text',
              required: false,
            }
          ],
        },
        layout: {
          background: '',
          backgroundType: 'color',
        },
        card: {
          background: '',
          border: '',
          borderRadius: '',
          shadow: '',
        },
        input: {
          background: '',
          border: '',
          focusBorder: '',
          focusRing: '',
          text: '',
          placeholder: '',
          borderRadius: '',
        },
        button: {
          primary: '',
          primaryText: '',
          primaryHover: '',
          disabledBackground: '',
          disabledText: '',
          borderRadius: '',
        },
        text: {
          primary: '',
          secondary: '',
          heading: '',
        },
        badge: {
          background: '',
          text: '',
          borderRadius: '',
        },
        icon: {
          primary: '',
          secondary: '',
        },
      },
    }
  });

  useEffect(() => {
    if (configuration) {
      form.reset({
        name: configuration.name || '',
        personalization: {
          logo: configuration.personalization?.logo || '',
          description: configuration.personalization?.description || '',
          informativeAnnouncement: (configuration.personalization as any)?.informativeAnnouncement || '',
          productModelConfig: {
            format: configuration.personalization?.productModelConfig?.format || 'custom',
            modelPlaceholder: configuration.personalization?.productModelConfig?.modelPlaceholder || '',
            typePlaceholder: configuration.personalization?.productModelConfig?.typePlaceholder || '',
            sizePlaceholder: configuration.personalization?.productModelConfig?.sizePlaceholder || '',
            customFields: (configuration.personalization?.productModelConfig?.customFields && configuration.personalization.productModelConfig.customFields.length > 0)
              ? configuration.personalization.productModelConfig.customFields
              : [
                {
                  key: 'model',
                  label: 'Model',
                  placeholder: 'Ex: Nom du produit, Référence...',
                  type: 'text',
                  required: false,
                }
              ],
          },
          layout: {
            background: (configuration.personalization as any)?.layout?.background || '',
            backgroundType: (configuration.personalization as any)?.layout?.backgroundType || 'color',
          },
          card: {
            background: configuration.personalization?.card?.background || '',
            border: configuration.personalization?.card?.border || '',
            borderRadius: configuration.personalization?.card?.borderRadius || '',
            shadow: configuration.personalization?.card?.shadow || '',
          },
          input: {
            background: configuration.personalization?.input?.background || '',
            border: configuration.personalization?.input?.border || '',
            focusBorder: configuration.personalization?.input?.focusBorder || '',
            focusRing: configuration.personalization?.input?.focusRing || '',
            text: configuration.personalization?.input?.text || '',
            placeholder: configuration.personalization?.input?.placeholder || '',
            borderRadius: configuration.personalization?.input?.borderRadius || '',
          },
          button: {
            primary: configuration.personalization?.button?.primary || '',
            primaryText: configuration.personalization?.button?.primaryText || '',
            primaryHover: configuration.personalization?.button?.primaryHover || '',
            disabledBackground: configuration.personalization?.button?.disabledBackground || '',
            disabledText: configuration.personalization?.button?.disabledText || '',
            borderRadius: configuration.personalization?.button?.borderRadius || '',
          },
          text: {
            primary: configuration.personalization?.text?.primary || '',
            secondary: configuration.personalization?.text?.secondary || '',
            heading: configuration.personalization?.text?.heading || '',
          },
          badge: {
            background: configuration.personalization?.badge?.background || '',
            text: configuration.personalization?.badge?.text || '',
            borderRadius: configuration.personalization?.badge?.borderRadius || '',
          },
          icon: {
            primary: configuration.personalization?.icon?.primary || '',
            secondary: configuration.personalization?.icon?.secondary || '',
          },
        },
      });
      setLogoPreview(getOrganizationLogoUrl(configuration) || null);
    }
  }, [configuration, form]);

  // Mettre à jour les valeurs temporaires pour prévisualisation en temps réel
  // Surveiller tous les changements dans personalization pour mise à jour immédiate
  const watchedPersonalization = useWatch({
    control: form.control,
    name: 'personalization',
  });

  useEffect(() => {
    // Mettre à jour les valeurs temporaires à chaque changement du formulaire
    // Extraire uniquement les valeurs de personnalisation visuelle (sans logo, description, productModelConfig)
    if (watchedPersonalization) {
      const { logo, productModelConfig, ...visualPersonalization } = watchedPersonalization;
      // Créer une nouvelle référence pour forcer le re-render même si les valeurs sont similaires
      // Cela garantit que React détecte les changements même dans des objets imbriqués
      const newPersonalization = {
        layout: visualPersonalization.layout ? { ...visualPersonalization.layout } : undefined,
        card: visualPersonalization.card ? { ...visualPersonalization.card } : undefined,
        input: visualPersonalization.input ? { ...visualPersonalization.input } : undefined,
        button: visualPersonalization.button ? { ...visualPersonalization.button } : undefined,
        text: visualPersonalization.text ? { ...visualPersonalization.text } : undefined,
        badge: visualPersonalization.badge ? { ...visualPersonalization.badge } : undefined,
        icon: visualPersonalization.icon ? { ...visualPersonalization.icon } : undefined,
        informativeAnnouncement: isEmptyHtml(visualPersonalization.informativeAnnouncement) ? '' : visualPersonalization.informativeAnnouncement,
        description: isEmptyHtml(visualPersonalization.description) ? '' : visualPersonalization.description,
      };
      // Filtrer les propriétés undefined pour ne garder que celles qui sont définies
      const filteredPersonalization = Object.fromEntries(
        Object.entries(newPersonalization).filter(([_, value]) => value !== undefined)
      );
      setTemporaryPersonalization(Object.keys(filteredPersonalization).length > 0 ? filteredPersonalization : null);
    } else {
      setTemporaryPersonalization(null);
    }
  }, [watchedPersonalization, setTemporaryPersonalization]);

  // Fonction pour appliquer un thème prédéfini et sauvegarder automatiquement
  const applyPresetTheme = async (themeKey: string) => {
    if (!organizationId) {
      toast.error(t('admin.configuration.general.missingOrgId'));
      return;
    }

    const themes: Record<string, any> = {
      default: {
        layout: { background: '#F9FAFB', backgroundType: 'color' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#9333EA', focusRing: '#9333EA', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: '#9333EA', primaryText: '#FFFFFF', primaryHover: '#7E22CE', disabledBackground: '#F3E8FF', disabledText: '#9333EA', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#F3E8FF', text: '#6B21A8', borderRadius: 'rounded-full' },
        icon: { primary: '#9333EA', secondary: '#6B7280' },
      },
      blue: {
        layout: { background: '#EFF6FF', backgroundType: 'color' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#2563EB', focusRing: '#2563EB', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: '#2563EB', primaryText: '#FFFFFF', primaryHover: '#1D4ED8', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#DBEAFE', text: '#1E40AF', borderRadius: 'rounded-full' },
        icon: { primary: '#2563EB', secondary: '#6B7280' },
      },
      green: {
        layout: { background: '#F0FDF4', backgroundType: 'color' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#059669', focusRing: '#059669', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: '#059669', primaryText: '#FFFFFF', primaryHover: '#047857', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#D1FAE5', text: '#047857', borderRadius: 'rounded-full' },
        icon: { primary: '#059669', secondary: '#6B7280' },
      },
      orange: {
        layout: { background: '#FFF7ED', backgroundType: 'color' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#F97316', focusRing: '#F97316', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: '#F97316', primaryText: '#FFFFFF', primaryHover: '#EA580C', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#FED7AA', text: '#9A3412', borderRadius: 'rounded-full' },
        icon: { primary: '#F97316', secondary: '#6B7280' },
      },
      'gradient-purple-pink': {
        layout: { background: 'linear-gradient(to bottom, #FDF4FF, #FCE7F3)', backgroundType: 'gradient' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#9333EA', focusRing: '#9333EA', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: 'linear-gradient(to right, #9333EA, #EC4899)', primaryText: '#FFFFFF', primaryHover: 'linear-gradient(to right, #7E22CE, #DB2777)', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#FDF4FF', text: '#9F1239', borderRadius: 'rounded-full' },
        icon: { primary: '#9333EA', secondary: '#6B7280' },
      },
      'gradient-blue-cyan': {
        layout: { background: 'linear-gradient(to bottom, #EFF6FF, #ECFEFF)', backgroundType: 'gradient' },
        card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
        input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#0891B2', focusRing: '#0891B2', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
        button: { primary: 'linear-gradient(to right, #2563EB, #06B6D4)', primaryText: '#FFFFFF', primaryHover: 'linear-gradient(to right, #1D4ED8, #0891B2)', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
        text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
        badge: { background: '#E0F2FE', text: '#0E7490', borderRadius: 'rounded-full' },
        icon: { primary: '#0891B2', secondary: '#6B7280' },
      },
    };

    const theme = themes[themeKey];
    if (!theme) return;

    // Appliquer le thème au formulaire
    Object.entries(theme).forEach(([section, values]: [string, any]) => {
      Object.entries(values).forEach(([key, value]: [string, any]) => {
        form.setValue(`personalization.${section}.${key}` as any, value, { shouldDirty: true });
      });
    });

    // Sauvegarder automatiquement
    setIsSaving(true);
    try {
      const personalization = { ...form.getValues('personalization') };

      // Nettoyer les contenus HTML vides pour éviter d'afficher des sections vides
      if (isEmptyHtml(personalization.description)) {
        personalization.description = '';
      }
      if (isEmptyHtml(personalization.informativeAnnouncement)) {
        personalization.informativeAnnouncement = '';
      }

      await OrgaConfigurationRepository.update(organizationId, {
        personalization,
      });
      toast.success(t('admin.configuration.general.updateSuccess'));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(t('admin.configuration.general.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        validateOrganizationLogoFile(file);
      } catch (error) {
        toast.error(getOrganizationLogoErrorMessage(error, t));
        event.target.value = '';
        return;
      }

      setLogoFile(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!organizationId) {
      toast.error(t('admin.configuration.general.missingOrgId'));
      return;
    }

    setIsSaving(true);
    try {
      let logoUrl = configuration?.personalization?.logo; // Conserver l'URL existante par défaut

      // Si un nouveau logo a été sélectionné, le télécharger
      if (logoFile) {
        logoUrl = await StorageService.uploadOrganizationLogo(logoFile, organizationId);
      }

      const data = form.getValues();
      const personalization = { ...data.personalization };

      // Nettoyer les contenus HTML vides pour éviter d'afficher des sections vides
      if (isEmptyHtml(personalization.description)) {
        personalization.description = '';
      }
      if (isEmptyHtml(personalization.informativeAnnouncement)) {
        personalization.informativeAnnouncement = '';
      }

      await OrgaConfigurationRepository.update(organizationId, {
        name: data.name,
        personalization: {
          ...personalization,
          logo: logoUrl,
        },
      });

      // Le listener Firebase se mettra automatiquement à jour avec les nouvelles valeurs
      // Réinitialiser les valeurs temporaires après un court délai pour laisser le temps à Firebase de mettre à jour
      // Cela permet d'éviter un flash visuel dans l'interface
      setTimeout(() => {
        setTemporaryPersonalization(null);
      }, 500);

      toast.success(t('admin.configuration.general.updateSuccess'));
      setLogoFile(null); // Réinitialiser le fichier sélectionné
      // Ne pas fermer l'éditeur, rester sur la même page
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(
        logoFile
          ? getOrganizationLogoErrorMessage(error, t)
          : t('admin.configuration.general.updateError'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const fields = [
      'personalization.layout.background', 'personalization.layout.backgroundType',
      'personalization.card.background', 'personalization.card.border', 'personalization.card.borderRadius', 'personalization.card.shadow',
      'personalization.input.background', 'personalization.input.border', 'personalization.input.focusBorder', 'personalization.input.focusRing',
      'personalization.input.text', 'personalization.input.placeholder', 'personalization.input.borderRadius',
      'personalization.button.primary', 'personalization.button.primaryText', 'personalization.button.primaryHover',
      'personalization.button.disabledBackground', 'personalization.button.disabledText', 'personalization.button.borderRadius',
      'personalization.text.primary', 'personalization.text.secondary', 'personalization.text.heading',
      'personalization.badge.background', 'personalization.badge.text', 'personalization.badge.borderRadius',
      'personalization.icon.primary', 'personalization.icon.secondary',
    ];
    fields.forEach(field => form.setValue(field as any, ''));
  };

  const handleClose = () => {
    searchParams.delete('edit');
    setSearchParams(searchParams);
  };

  return (
    <div
      className="fixed left-0 top-[66px] lg:top-[76px] bottom-0 w-full max-w-[90vw] sm:max-w-[420px] lg:w-[420px] shadow-2xl z-[60] flex flex-col overflow-hidden"
      style={{
        backgroundColor: theme.colors.backgroundSecondary || '#F9FAFB',
        borderRight: `1px solid ${theme.colors.border || '#E5E7EB'}`,
      }}
    >
      {/* Header fixe */}
      <div
        className="flex items-center justify-between p-3 sm:p-4 border-b flex-shrink-0"
        style={{
          backgroundColor: theme.colors.background || '#FFFFFF',
          borderBottomColor: theme.colors.border || '#E5E7EB',
        }}
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <button
            onClick={() => {
              if (idorga) {
                navigate(`/orga/${idorga}/admin/configuration#personalization`);
              }
            }}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{
              color: theme.colors.foregroundSecondary || '#6B7280',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title={t('admin.configuration.personalization.editor.backToConfig')}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {/* Bouton fermer sur mobile - Plus visible avec texte */}
          <button
            onClick={handleClose}
            className="lg:hidden ml-auto flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{
              color: theme.colors.foreground || '#111827',
              backgroundColor: theme.colors.backgroundTertiary || '#F3F4F6',
              border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary || '#F9FAFB';
              e.currentTarget.style.borderColor = theme.colors.border || '#D1D5DB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
              e.currentTarget.style.borderColor = theme.colors.border || '#E5E7EB';
            }}
            title="Fermer"
            aria-label="Fermer"
          >
            <span className="text-sm font-medium" style={{ color: theme.colors.foreground || '#111827' }}>Fermer</span>
            <X className="w-4 h-4" style={{ color: theme.colors.foreground || '#111827' }} />
          </button>
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: (() => {
                const iconPrimary = form.watch('personalization.icon.primary') || theme.colors.primary || '#9333EA';
                // Convertir la couleur en rgba avec opacité pour le background
                if (iconPrimary.startsWith('#')) {
                  const hex = iconPrimary.replace('#', '');
                  if (hex.length === 6) {
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    return `rgba(${r}, ${g}, ${b}, 0.1)`;
                  }
                } else if (iconPrimary.startsWith('rgba')) {
                  // Si c'est déjà en rgba, extraire les valeurs RGB et appliquer l'opacité
                  const match = iconPrimary.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  if (match) {
                    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, 0.1)`;
                  }
                }
                // Fallback: utiliser la couleur avec opacité hex
                return iconPrimary + '1A'; // 1A = ~10% d'opacité en hex
              })()
            }}
          >
            <Settings className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: form.watch('personalization.icon.primary') || theme.colors.primary || '#9333EA' }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold truncate" style={{ color: theme.colors.foreground || '#111827' }}>
              {t('admin.configuration.personalization.editor.title')}
            </h3>
            <p className="text-xs mt-0.5 truncate hidden sm:block" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
              {t('admin.configuration.personalization.editor.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ minWidth: 0 }}>
        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          {/* Section Générale (Nom, Description, Logo) */}
          <div
            className="rounded-t-lg border overflow-hidden"
            style={{
              minWidth: 0,
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#E5E7EB',
            }}
          >
            <button
              type="button"
              onClick={() => toggleSection('general')}
              className="w-full flex items-center justify-between p-3 transition-colors"
              style={{
                backgroundColor: expandedSections.general ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!expandedSections.general) {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                }
              }}
              onMouseLeave={(e) => {
                if (!expandedSections.general) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex-1 min-w-0 text-left">
                <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                  {t('admin.configuration.personalization.editor.sections.general')}
                </h4>
              </div>
              {expandedSections.general ? (
                <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              ) : (
                <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              )}
            </button>
            {expandedSections.general && (
              <div className="px-4 py-4 space-y-4" style={{ minWidth: 0 }}>
                {/* Logo */}
                <div>
                  <label htmlFor="logo" className="block text-xs font-medium mb-2" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.personalization.editor.fields.logo')}
                  </label>
                  <div className="flex flex-col items-start space-y-3" style={{ minWidth: 0 }}>
                    <div
                      className="w-20 h-20 border-2 rounded-lg overflow-hidden flex-shrink-0"
                      style={{
                        borderColor: theme.colors.border || '#D1D5DB',
                        backgroundColor: theme.colors.backgroundTertiary || '#F3F4F6',
                      }}
                    >
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ color: theme.colors.foregroundSecondary || '#9CA3AF' }}>
                          <Image className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept={ORGANIZATION_LOGO_ACCEPT}
                      onChange={handleLogoChange}
                      className="block w-full min-w-0 text-sm file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-10 file:text-primary hover:file:bg-primary-20"
                      style={{
                        maxWidth: '100%',
                        color: theme.colors.foregroundSecondary || '#6B7280',
                      }}
                    />
                  </div>
                </div>

                {/* Nom */}
                <div>
                  <label htmlFor="organizationName" className="block text-xs font-medium mb-1" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.personalization.editor.fields.organizationName')}
                  </label>
                  <input
                    id="organizationName"
                    type="text"
                    {...form.register('name', { required: false })}
                    className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                    placeholder={t('admin.configuration.personalization.editor.fields.organizationNamePlaceholder')}
                    style={{
                      maxWidth: '100%',
                      borderColor: theme.colors.border || '#D1D5DB',
                      backgroundColor: theme.colors.background || '#FFFFFF',
                      color: theme.colors.foreground || '#111827',
                    }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-xs font-medium mb-1" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.personalization.editor.fields.description')}
                  </label>
                  <RichTextEditor
                    value={form.watch('personalization.description') || ''}
                    onChange={(value) => form.setValue('personalization.description', value, { shouldDirty: true })}
                    placeholder={t('admin.configuration.personalization.editor.fields.descriptionPlaceholder')}
                    theme={theme}
                  />
                </div>

                {/* Annonce informative */}
                <div>
                  <label htmlFor="informativeAnnouncement" className="block text-xs font-medium mb-1" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.general.informativeAnnouncement')}
                  </label>
                  <RichTextEditor
                    value={form.watch('personalization.informativeAnnouncement') || ''}
                    onChange={(value) => form.setValue('personalization.informativeAnnouncement', value, { shouldDirty: true })}
                    placeholder={t('admin.configuration.general.informativeAnnouncementPlaceholder')}
                    theme={theme}
                  />
                  <p className="mt-1 text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                    {t('admin.configuration.general.informativeAnnouncementTooltip')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Format du champ modèle produit */}
          <div
            className="border-l border-r overflow-hidden"
            style={{
              minWidth: 0,
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#E5E7EB',
              borderTop: 'none',
            }}
          >
            <button
              type="button"
              onClick={() => toggleSection('productModel')}
              className="w-full flex items-center justify-between p-3 transition-colors"
              style={{
                backgroundColor: expandedSections.productModel ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!expandedSections.productModel) {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                }
              }}
              onMouseLeave={(e) => {
                if (!expandedSections.productModel) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex-1 min-w-0 text-left">
                <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                  {t('admin.configuration.personalization.editor.sections.productModel')}
                </h4>
                {!expandedSections.productModel && (
                  <div className="text-xs mt-0.5 space-y-0.5" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                    <div className="truncate">
                      {(() => {
                        const customFields = form.watch('personalization.productModelConfig.customFields') || [];
                        if (customFields.length > 0) {
                          const fieldLabels = customFields.slice(0, 3).map((field: any) => field.label || field.key || '').filter(Boolean);
                          if (fieldLabels.length > 0) {
                            return fieldLabels.join(', ');
                          }
                        }
                        return 'Aucun champ';
                      })()}
                    </div>
                    {(form.watch('personalization.productModelConfig.customFields') || []).length > 3 && (
                      <div className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                        +{(form.watch('personalization.productModelConfig.customFields') || []).length - 3} autre{(form.watch('personalization.productModelConfig.customFields') || []).length - 3 > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {expandedSections.productModel ? (
                <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              ) : (
                <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              )}
            </button>
            {expandedSections.productModel && (
              <div className="px-4 py-4 space-y-4" style={{ minWidth: 0 }}>
                {/* Modèles prédéfinis */}
                <div className="space-y-2">
                  <label htmlFor="fieldTemplate" className="block text-xs font-medium mb-2" style={{ color: theme.colors.foreground || '#111827' }}>Modèles par secteur</label>
                  <select
                    id="fieldTemplate"
                    onChange={(e) => {
                      const templateKey = e.target.value;
                      if (templateKey && templateKey !== 'custom') {
                        const templates: Record<string, Array<{ key: string; label: string; placeholder: string; type: string; required: boolean }>> = {
                          'pret-a-porter': [
                            { key: 'model', label: 'Modèle', placeholder: 'Ex: Robe été, Pantalon slim...', type: 'text', required: true },
                            { key: 'taille', label: 'Taille', placeholder: 'Ex: S, M, L, XL...', type: 'text', required: true }
                          ],
                          'bonbons': [
                            { key: 'type', label: 'Type', placeholder: 'Ex: Haribo, Malabar, Chupa Chups...', type: 'text', required: true },
                            { key: 'gramme', label: 'Grammes', placeholder: 'Ex: 100, 200, 500', type: 'number', required: true }
                          ],
                          'electronique': [
                            { key: 'modele', label: 'Modèle', placeholder: 'Ex: iPhone 15, MacBook Pro...', type: 'text', required: true },
                            { key: 'capacite', label: 'Capacité', placeholder: 'Ex: 128GB, 256GB, 512GB...', type: 'text', required: false }
                          ],
                          'cosmetique': [
                            { key: 'produit', label: 'Produit', placeholder: 'Ex: Crème hydratante, Rouge à lèvres...', type: 'text', required: true },
                            { key: 'quantite', label: 'Quantité', placeholder: 'Ex: 50ml, 100ml, 30ml...', type: 'text', required: false }
                          ],
                          'livres': [
                            { key: 'titre', label: 'Titre', placeholder: 'Ex: Le Seigneur des Anneaux...', type: 'textarea', required: true },
                            { key: 'format', label: 'Format', placeholder: 'Ex: Broché, Poche, Ebook...', type: 'text', required: false }
                          ],
                          'accessoires': [
                            { key: 'type', label: 'Type', placeholder: 'Ex: Sac, Montre, Bijou...', type: 'text', required: true },
                            { key: 'couleur', label: 'Couleur', placeholder: 'Ex: Noir, Blanc, Rouge...', type: 'text', required: false }
                          ],
                          'alimentaire': [
                            { key: 'produit', label: 'Produit', placeholder: 'Ex: Pizza, Burger, Salade...', type: 'text', required: true },
                            { key: 'portion', label: 'Portion', placeholder: 'Ex: Petite, Moyenne, Grande...', type: 'text', required: false }
                          ],
                          'simple': [
                            { key: 'model', label: 'Modèle', placeholder: 'Ex: Nom du produit, Référence...', type: 'text', required: false }
                          ]
                        };
                        const template = templates[templateKey];
                        if (template) {
                          form.setValue('personalization.productModelConfig.customFields', template);
                          e.target.value = 'custom'; // Reset select
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-md text-xs"
                    style={{
                      borderColor: theme.colors.border || '#D1D5DB',
                      backgroundColor: theme.colors.background || '#FFFFFF',
                      color: theme.colors.foreground || '#111827',
                    }}
                    defaultValue="custom"
                  >
                    <option value="custom">Personnalisé (actuel)</option>
                    <option value="pret-a-porter">👔 Prêt-à-porter (Modèle + Taille)</option>
                    <option value="bonbons">🍬 Bonbons (Type + Grammes)</option>
                    <option value="electronique">📱 Électronique (Modèle + Capacité)</option>
                    <option value="cosmetique">💄 Cosmétique (Produit + Quantité)</option>
                    <option value="livres">📚 Livres (Titre + Format)</option>
                    <option value="accessoires">👝 Accessoires (Type + Couleur)</option>
                    <option value="alimentaire">🍕 Alimentaire (Produit + Portion)</option>
                    <option value="simple">📝 Simple (Modèle uniquement)</option>
                  </select>
                </div>

                {/* Champs personnalisés */}
                <CustomFieldsList form={form} theme={theme} />
              </div>
            )}
          </div>

          {/* Thème */}
          <div
            className="overflow-hidden"
            style={{
              minWidth: 0,
              backgroundColor: theme.colors.background || '#FFFFFF',
              borderColor: theme.colors.border || '#E5E7EB',
              border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
              borderTop: 'none',
            }}
          >
            <button
              type="button"
              onClick={() => toggleSection('theme')}
              className="w-full flex items-center justify-between p-3 transition-colors"
              style={{
                backgroundColor: expandedSections.theme ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!expandedSections.theme) {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                }
              }}
              onMouseLeave={(e) => {
                if (!expandedSections.theme) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex-1 min-w-0 text-left">
                <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                  {t('admin.configuration.personalization.editor.sections.theme')}
                </h4>
                {!expandedSections.theme && (
                  <div className="text-xs mt-0.5 truncate" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                    {(() => {
                      const currentValues = form.getValues('personalization');
                      const themes: Record<string, any> = {
                        default: { button: { primary: '#9333EA' } },
                        blue: { button: { primary: '#2563EB' } },
                        green: { button: { primary: '#059669' } },
                        orange: { button: { primary: '#F97316' } },
                      };
                      for (const [key, theme] of Object.entries(themes)) {
                        if (currentValues?.button?.primary === theme.button.primary) {
                          return key === 'default' ? 'Violet' : key.charAt(0).toUpperCase() + key.slice(1);
                        }
                      }
                      return 'Personnalisé';
                    })()}
                  </div>
                )}
              </div>
              {expandedSections.theme ? (
                <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              ) : (
                <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
              )}
            </button>
            {expandedSections.theme && (
              <div className="px-4 pb-4" style={{ minWidth: 0 }}>
                {/* Thèmes prédéfinis */}
                <div className="mb-4">
                  <label htmlFor="presetTheme" className="block text-sm font-medium mb-2" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.personalization.editor.fields.presetThemes')}
                  </label>
                  <select
                    id="presetTheme"
                    value={(() => {
                      // Détecter si les valeurs actuelles correspondent à un thème prédéfini
                      const currentValues = form.getValues('personalization');
                      const themes: Record<string, any> = {
                        default: {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#9333EA', focusRing: '#9333EA', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: '#9333EA', primaryText: '#FFFFFF', primaryHover: '#7E22CE', disabledBackground: '#F3E8FF', disabledText: '#9333EA', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#F3E8FF', text: '#6B21A8', borderRadius: 'rounded-full' },
                          icon: { primary: '#9333EA', secondary: '#6B7280' },
                        },
                        blue: {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#2563EB', focusRing: '#2563EB', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: '#2563EB', primaryText: '#FFFFFF', primaryHover: '#1D4ED8', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#DBEAFE', text: '#1E40AF', borderRadius: 'rounded-full' },
                          icon: { primary: '#2563EB', secondary: '#6B7280' },
                        },
                        green: {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#059669', focusRing: '#059669', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: '#059669', primaryText: '#FFFFFF', primaryHover: '#047857', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#D1FAE5', text: '#047857', borderRadius: 'rounded-full' },
                          icon: { primary: '#059669', secondary: '#6B7280' },
                        },
                        orange: {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#F97316', focusRing: '#F97316', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: '#F97316', primaryText: '#FFFFFF', primaryHover: '#EA580C', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#FED7AA', text: '#9A3412', borderRadius: 'rounded-full' },
                          icon: { primary: '#F97316', secondary: '#6B7280' },
                        },
                        'gradient-purple-pink': {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#9333EA', focusRing: '#9333EA', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: 'linear-gradient(to right, #9333EA, #EC4899)', primaryText: '#FFFFFF', primaryHover: 'linear-gradient(to right, #7E22CE, #DB2777)', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#FDF4FF', text: '#9F1239', borderRadius: 'rounded-full' },
                          icon: { primary: '#9333EA', secondary: '#6B7280' },
                        },
                        'gradient-blue-cyan': {
                          card: { background: '#FFFFFF', border: '#E5E7EB', borderRadius: 'rounded-3xl', shadow: 'shadow-lg' },
                          input: { background: '#FFFFFF', border: '#D1D5DB', focusBorder: '#0891B2', focusRing: '#0891B2', text: '#111827', placeholder: '#9CA3AF', borderRadius: 'rounded-2xl' },
                          button: { primary: 'linear-gradient(to right, #2563EB, #06B6D4)', primaryText: '#FFFFFF', primaryHover: 'linear-gradient(to right, #1D4ED8, #0891B2)', disabledBackground: '#D1D5DB', disabledText: '#6B7280', borderRadius: 'rounded-2xl' },
                          text: { primary: '#111827', secondary: '#6B7280', heading: '#111827' },
                          badge: { background: '#E0F2FE', text: '#0E7490', borderRadius: 'rounded-full' },
                          icon: { primary: '#0891B2', secondary: '#6B7280' },
                        },
                      };

                      // Comparer les valeurs actuelles avec chaque thème
                      for (const [key, themeData] of Object.entries(themes)) {
                        let matches = true;
                        for (const [section, values] of Object.entries(themeData as Record<string, any>)) {
                          for (const [field, value] of Object.entries(values as Record<string, any>)) {
                            const currentValue = (currentValues as any)?.[section]?.[field] || '';
                            if (currentValue !== value && currentValue !== '') {
                              matches = false;
                              break;
                            }
                          }
                          if (!matches) break;
                        }
                        if (matches) return key;
                      }
                      return 'custom';
                    })()}
                    onChange={async (e) => {
                      const themeKey = e.target.value;
                      if (themeKey && themeKey !== 'custom') {
                        await applyPresetTheme(themeKey);
                      }
                    }}
                    className="w-full min-w-0 px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary text-sm text-foreground bg-surface"
                    style={{
                      maxWidth: '100%',
                      color: theme.colors.foreground || '#111827',
                      backgroundColor: theme.colors.background || '#FFFFFF',
                      borderColor: theme.colors.border || '#D1D5DB',
                    }}
                  >
                    <option value="custom">{t('admin.configuration.personalization.editor.fields.presetThemeCustom')}</option>
                    <option value="default">{t('admin.configuration.personalization.editor.fields.presetThemeDefault')}</option>
                    <option value="blue">{t('admin.configuration.personalization.editor.fields.presetThemeBlue')}</option>
                    <option value="green">{t('admin.configuration.personalization.editor.fields.presetThemeGreen')}</option>
                    <option value="orange">{t('admin.configuration.personalization.editor.fields.presetThemeOrange')}</option>
                    <option value="gradient-purple-pink">{t('admin.configuration.personalization.editor.fields.presetThemeGradientPurplePink')}</option>
                    <option value="gradient-blue-cyan">{t('admin.configuration.personalization.editor.fields.presetThemeGradientBlueCyan')}</option>
                  </select>
                </div>
              </div>
            )}

            {/* Background du layout - Nouvelle section expandable */}
            <div
              className="overflow-hidden"
              style={{
                minWidth: 0,
                backgroundColor: theme.colors.background || '#FFFFFF',
                borderColor: theme.colors.border || '#E5E7EB',
                border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                borderTop: 'none',
              }}
            >
              <button
                type="button"
                onClick={() => toggleSection('layoutBackground')}
                className="w-full flex items-center justify-between p-3 transition-colors"
                style={{
                  backgroundColor: expandedSections.layoutBackground ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!expandedSections.layoutBackground) {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!expandedSections.layoutBackground) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className="flex-1 min-w-0 text-left">
                  <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                    {t('admin.configuration.personalization.editor.sections.layoutBackground')}
                  </h4>
                  {!expandedSections.layoutBackground && (
                    <div className="flex items-center gap-1.5 mt-1 justify-start">
                      {(() => {
                        const bgType = form.watch('personalization.layout.backgroundType') || 'color';
                        const bgValue = form.watch('personalization.layout.background') || '#FFFFFF';
                        if (bgType === 'image') {
                          return <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>{t('admin.configuration.personalization.editor.fields.backgroundTypeImage')}</span>;
                        } else if (bgType === 'gradient') {
                          return <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>{t('admin.configuration.personalization.editor.fields.backgroundTypeGradient')}</span>;
                        } else {
                          return (
                            <>
                              <div
                                className="w-3 h-3 rounded border flex-shrink-0"
                                style={{
                                  backgroundColor: bgValue,
                                  borderColor: theme.colors.border || '#D1D5DB',
                                }}
                              />
                              <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>{bgValue}</span>
                            </>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
                {expandedSections.layoutBackground ? (
                  <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                ) : (
                  <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                )}
              </button>
              {expandedSections.layoutBackground && (
                <div className="px-4 py-4" style={{ minWidth: 0 }}>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="backgroundType" className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                        {t('admin.configuration.personalization.editor.fields.backgroundType')}
                      </label>
                      <select
                        id="backgroundType"
                        {...form.register('personalization.layout.backgroundType')}
                        className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                        style={{
                          borderColor: theme.colors.border || '#D1D5DB',
                          backgroundColor: theme.colors.background || '#FFFFFF',
                          color: theme.colors.foreground || '#111827',
                        }}
                      >
                        <option value="color">{t('admin.configuration.personalization.editor.fields.backgroundTypeColor')}</option>
                        <option value="gradient">{t('admin.configuration.personalization.editor.fields.backgroundTypeGradient')}</option>
                        <option value="image">{t('admin.configuration.personalization.editor.fields.backgroundTypeImage')}</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="backgroundValue" className="block text-xs mb-1" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                        {form.watch('personalization.layout.backgroundType') === 'image' ? t('admin.configuration.personalization.editor.fields.backgroundImageUrl') :
                          form.watch('personalization.layout.backgroundType') === 'gradient' ? t('admin.configuration.personalization.editor.fields.backgroundGradient') :
                            t('admin.configuration.personalization.editor.fields.backgroundColor')}
                      </label>
                      {form.watch('personalization.layout.backgroundType') === 'color' && (
                        <ColorOrGradientInput
                          value={form.watch('personalization.layout.background') || ''}
                          onChange={(value) => form.setValue('personalization.layout.background', value)}
                          defaultColor="#FFFFFF"
                          label={t('admin.configuration.personalization.editor.fields.backgroundColor')}
                          theme={theme}
                          forceMode="solid"
                        />
                      )}
                      {form.watch('personalization.layout.backgroundType') === 'gradient' && (
                        <ColorOrGradientInput
                          value={form.watch('personalization.layout.background') || ''}
                          onChange={(value) => form.setValue('personalization.layout.background', value)}
                          defaultColor="#9333EA"
                          label={t('admin.configuration.personalization.editor.fields.backgroundGradient')}
                          theme={theme}
                          forceMode="gradient"
                        />
                      )}
                      {form.watch('personalization.layout.backgroundType') === 'image' && (
                        <input
                          id="backgroundValue"
                          type="text"
                          {...form.register('personalization.layout.background')}
                          className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                          style={{
                            borderColor: theme.colors.border || '#D1D5DB',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            color: theme.colors.foreground || '#111827',
                          }}
                          placeholder="https://..."
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sections de personnalisation */}
            <div style={{ minWidth: 0 }}>
              {/* Cartes */}
              <div
                className="overflow-hidden rounded-t-lg"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('cards')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.cards ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.cards) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.cards) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.cards')}
                    </h4>
                    {!expandedSections.cards && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.card.background') || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.background')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.card.border') || '#E5E7EB',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.border')}
                          </span>
                        </div>
                        {(form.watch('personalization.card.borderRadius') || 'rounded-3xl') && (
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {form.watch('personalization.card.borderRadius') || 'rounded-3xl'}
                          </span>
                        )}
                        {(form.watch('personalization.card.shadow') || 'shadow-lg') && (
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {form.watch('personalization.card.shadow') || 'shadow-lg'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {expandedSections.cards ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.cards && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="cardBackground" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.cardBackground')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="cardBackgroundColor"
                            type="color"
                            value={form.watch('personalization.card.background') || '#FFFFFF'}
                            onChange={(e) => form.setValue('personalization.card.background', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.cardBackground')}
                          />
                          <input
                            id="cardBackground"
                            type="text"
                            value={form.watch('personalization.card.background') || ''}
                            onChange={(e) => form.setValue('personalization.card.background', e.target.value)}
                            placeholder="#FFFFFF"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="cardBorder" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.cardBorder')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="cardBorderColor"
                            type="color"
                            value={form.watch('personalization.card.border') || '#E5E7EB'}
                            onChange={(e) => form.setValue('personalization.card.border', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.cardBorder')}
                          />
                          <input
                            id="cardBorder"
                            type="text"
                            value={form.watch('personalization.card.border') || ''}
                            onChange={(e) => form.setValue('personalization.card.border', e.target.value)}
                            placeholder="#E5E7EB"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="cardBorderRadius" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.cardBorderRadius')}
                        </label>
                        <select
                          id="cardBorderRadius"
                          value={form.watch('personalization.card.borderRadius') || ''}
                          onChange={(e) => form.setValue('personalization.card.borderRadius', e.target.value)}
                          className="w-full min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                          style={{
                            color: theme.colors.foreground || '#111827',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            borderColor: theme.colors.border || '#D1D5DB',
                          }}
                        >
                          <option value="">{'rounded-3xl (défaut)'}</option>
                          <option value="rounded-sm">rounded-sm</option>
                          <option value="rounded-md">rounded-md</option>
                          <option value="rounded-lg">rounded-lg</option>
                          <option value="rounded-xl">rounded-xl</option>
                          <option value="rounded-2xl">rounded-2xl</option>
                          <option value="rounded-3xl">rounded-3xl</option>
                          <option value="rounded-full">rounded-full</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="cardShadow" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.cardShadow')}
                        </label>
                        <select
                          id="cardShadow"
                          value={form.watch('personalization.card.shadow') || ''}
                          onChange={(e) => form.setValue('personalization.card.shadow', e.target.value)}
                          className="w-full min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                          style={{
                            color: theme.colors.foreground || '#111827',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            borderColor: theme.colors.border || '#D1D5DB',
                          }}
                        >
                          <option value="">shadow-lg (défaut)</option>
                          <option value="shadow-none">Aucune ombre</option>
                          <option value="shadow-sm">Ombre légère</option>
                          <option value="shadow">Ombre normale</option>
                          <option value="shadow-md">Ombre moyenne</option>
                          <option value="shadow-lg">Ombre large</option>
                          <option value="shadow-xl">Ombre très large</option>
                          <option value="shadow-2xl">Ombre extra large</option>
                          <option value="shadow-inner">Ombre intérieure</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Inputs */}
              <div
                className="overflow-hidden"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('inputs')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.inputs ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.inputs) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.inputs) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.inputs')}
                    </h4>
                    {!expandedSections.inputs && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.input.background') || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.background')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.input.border') || '#D1D5DB',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.border')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.input.focusBorder') || '#9333EA',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.focus')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.input.text') || '#111827',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.text')}
                          </span>
                        </div>
                        {(form.watch('personalization.input.borderRadius') || 'rounded-2xl') && (
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {form.watch('personalization.input.borderRadius') || 'rounded-2xl'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {expandedSections.inputs ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.inputs && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="inputBackground" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputBackground')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputBackgroundColor"
                            type="color"
                            value={form.watch('personalization.input.background') || '#FFFFFF'}
                            onChange={(e) => form.setValue('personalization.input.background', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.inputBackground')}
                          />
                          <input
                            id="inputBackground"
                            type="text"
                            value={form.watch('personalization.input.background') || ''}
                            onChange={(e) => form.setValue('personalization.input.background', e.target.value)}
                            placeholder="#FFFFFF"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputBorder" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputBorder')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputBorderColor"
                            type="color"
                            value={form.watch('personalization.input.border') || '#D1D5DB'}
                            onChange={(e) => form.setValue('personalization.input.border', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.inputBorder')}
                          />
                          <input
                            id="inputBorder"
                            type="text"
                            value={form.watch('personalization.input.border') || ''}
                            onChange={(e) => form.setValue('personalization.input.border', e.target.value)}
                            placeholder="#D1D5DB"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputFocusBorder" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputFocusBorder')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputFocusBorderColor"
                            type="color"
                            value={form.watch('personalization.input.focusBorder') || '#9333EA'}
                            onChange={(e) => form.setValue('personalization.input.focusBorder', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.inputFocusBorder')}
                          />
                          <input
                            id="inputFocusBorder"
                            type="text"
                            value={form.watch('personalization.input.focusBorder') || ''}
                            onChange={(e) => form.setValue('personalization.input.focusBorder', e.target.value)}
                            placeholder="#9333EA"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputFocusRing" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputFocusRing')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputFocusRingColor"
                            type="color"
                            value={form.watch('personalization.input.focusRing') || '#9333EA'}
                            onChange={(e) => form.setValue('personalization.input.focusRing', e.target.value)}
                            className="personalization-color-picker"
                          />
                          <input
                            type="text"
                            value={form.watch('personalization.input.focusRing') || ''}
                            onChange={(e) => form.setValue('personalization.input.focusRing', e.target.value)}
                            placeholder="#9333EA"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputText" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputText')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputTextColor"
                            type="color"
                            value={form.watch('personalization.input.text') || '#111827'}
                            onChange={(e) => form.setValue('personalization.input.text', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.inputText')}
                          />
                          <input
                            id="inputText"
                            type="text"
                            value={form.watch('personalization.input.text') || ''}
                            onChange={(e) => form.setValue('personalization.input.text', e.target.value)}
                            placeholder="#111827"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputPlaceholder" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputPlaceholder')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="inputPlaceholderColor"
                            type="color"
                            value={form.watch('personalization.input.placeholder') || '#9CA3AF'}
                            onChange={(e) => form.setValue('personalization.input.placeholder', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.inputPlaceholder')}
                          />
                          <input
                            id="inputPlaceholder"
                            type="text"
                            value={form.watch('personalization.input.placeholder') || ''}
                            onChange={(e) => form.setValue('personalization.input.placeholder', e.target.value)}
                            placeholder="#9CA3AF"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="inputBorderRadius" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.inputBorderRadius')}
                        </label>
                        <select
                          id="inputBorderRadius"
                          value={form.watch('personalization.input.borderRadius') || ''}
                          onChange={(e) => form.setValue('personalization.input.borderRadius', e.target.value)}
                          className="w-full min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                          style={{
                            color: theme.colors.foreground || '#111827',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            borderColor: theme.colors.border || '#D1D5DB',
                          }}
                        >
                          <option value="">{'rounded-2xl (défaut)'}</option>
                          <option value="rounded-sm">rounded-sm</option>
                          <option value="rounded-md">rounded-md</option>
                          <option value="rounded-lg">rounded-lg</option>
                          <option value="rounded-xl">rounded-xl</option>
                          <option value="rounded-2xl">rounded-2xl</option>
                          <option value="rounded-3xl">rounded-3xl</option>
                          <option value="rounded-full">rounded-full</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div
                className="overflow-hidden"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('buttons')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.buttons ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.buttons) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.buttons) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.buttons')}
                    </h4>
                    {!expandedSections.buttons && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          {(() => {
                            const primaryColor = form.watch('personalization.button.primary') || '#9333EA';
                            const isGradient = primaryColor.includes('gradient') || primaryColor.includes('linear-gradient');
                            return isGradient ? (
                              <div
                                className="w-3 h-3 rounded border flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-500"
                                style={{ borderColor: theme.colors.border || '#D1D5DB' }}
                              />
                            ) : (
                              <div
                                className="w-3 h-3 rounded border flex-shrink-0"
                                style={{
                                  backgroundColor: primaryColor,
                                  borderColor: theme.colors.border || '#D1D5DB',
                                }}
                              />
                            );
                          })()}
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.primary')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.button.primaryText') || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.text')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.button.disabledBackground') || '#D1D5DB',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.disabled')}
                          </span>
                        </div>
                        {(form.watch('personalization.button.borderRadius') || 'rounded-2xl') && (
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {form.watch('personalization.button.borderRadius') || 'rounded-2xl'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {expandedSections.buttons ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.buttons && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="buttonPrimary" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonPrimary')}
                        </label>
                        <ColorOrGradientInput
                          value={form.watch('personalization.button.primary') || ''}
                          onChange={(value) => form.setValue('personalization.button.primary', value)}
                          defaultColor="#9333EA"
                          label={t('admin.configuration.personalization.editor.fields.buttonPrimary')}
                          theme={theme}
                        />
                      </div>
                      <div>
                        <label htmlFor="buttonPrimaryText" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonPrimaryText')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="buttonPrimaryTextColor"
                            type="color"
                            value={form.watch('personalization.button.primaryText') || '#FFFFFF'}
                            onChange={(e) => form.setValue('personalization.button.primaryText', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.buttonPrimaryText')}
                          />
                          <input
                            id="buttonPrimaryText"
                            type="text"
                            value={form.watch('personalization.button.primaryText') || ''}
                            onChange={(e) => form.setValue('personalization.button.primaryText', e.target.value)}
                            placeholder="#FFFFFF"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="buttonPrimaryHover" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonPrimaryHover')}
                        </label>
                        <ColorOrGradientInput
                          value={form.watch('personalization.button.primaryHover') || ''}
                          onChange={(value) => form.setValue('personalization.button.primaryHover', value)}
                          defaultColor="#7E22CE"
                          label={t('admin.configuration.personalization.editor.fields.buttonPrimaryHover')}
                          theme={theme}
                        />
                      </div>
                      <div>
                        <label htmlFor="buttonDisabledBackground" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonDisabledBackground')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="buttonDisabledBackgroundColor"
                            type="color"
                            value={form.watch('personalization.button.disabledBackground') || '#D1D5DB'}
                            onChange={(e) => form.setValue('personalization.button.disabledBackground', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.buttonDisabledBackground')}
                          />
                          <input
                            id="buttonDisabledBackground"
                            type="text"
                            value={form.watch('personalization.button.disabledBackground') || ''}
                            onChange={(e) => form.setValue('personalization.button.disabledBackground', e.target.value)}
                            placeholder="#D1D5DB"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="buttonDisabledText" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonDisabledText')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="buttonDisabledTextColor"
                            type="color"
                            value={form.watch('personalization.button.disabledText') || '#6B7280'}
                            onChange={(e) => form.setValue('personalization.button.disabledText', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.buttonDisabledText')}
                          />
                          <input
                            id="buttonDisabledText"
                            type="text"
                            value={form.watch('personalization.button.disabledText') || ''}
                            onChange={(e) => form.setValue('personalization.button.disabledText', e.target.value)}
                            placeholder="#6B7280"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="buttonBorderRadius" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.buttonBorderRadius')}
                        </label>
                        <select
                          id="buttonBorderRadius"
                          value={form.watch('personalization.button.borderRadius') || ''}
                          onChange={(e) => form.setValue('personalization.button.borderRadius', e.target.value)}
                          className="w-full min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                          style={{
                            color: theme.colors.foreground || '#111827',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            borderColor: theme.colors.border || '#D1D5DB',
                          }}
                        >
                          <option value="">{'rounded-2xl (défaut)'}</option>
                          <option value="rounded-sm">rounded-sm</option>
                          <option value="rounded-md">rounded-md</option>
                          <option value="rounded-lg">rounded-lg</option>
                          <option value="rounded-xl">rounded-xl</option>
                          <option value="rounded-2xl">rounded-2xl</option>
                          <option value="rounded-3xl">rounded-3xl</option>
                          <option value="rounded-full">rounded-full</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Textes */}
              <div
                className="overflow-hidden"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('texts')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.texts ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.texts) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.texts) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.texts')}
                    </h4>
                    {!expandedSections.texts && (
                      <div className="flex flex-wrap items-center gap-2 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.text.primary') || '#111827',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.fields.textPrimary')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.text.secondary') || '#6B7280',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.fields.textSecondary')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {expandedSections.texts ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.texts && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="textPrimary" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.textPrimary')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="textPrimaryColor"
                            type="color"
                            value={form.watch('personalization.text.primary') || '#111827'}
                            onChange={(e) => form.setValue('personalization.text.primary', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.textPrimary')}
                          />
                          <input
                            id="textPrimary"
                            type="text"
                            value={form.watch('personalization.text.primary') || ''}
                            onChange={(e) => form.setValue('personalization.text.primary', e.target.value)}
                            placeholder="#111827"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="textSecondary" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.textSecondary')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="textSecondaryColor"
                            type="color"
                            value={form.watch('personalization.text.secondary') || '#6B7280'}
                            onChange={(e) => form.setValue('personalization.text.secondary', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.textSecondary')}
                          />
                          <input
                            id="textSecondary"
                            type="text"
                            value={form.watch('personalization.text.secondary') || ''}
                            onChange={(e) => form.setValue('personalization.text.secondary', e.target.value)}
                            placeholder="#6B7280"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="textHeading" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.textHeading')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="textHeadingColor"
                            type="color"
                            value={form.watch('personalization.text.heading') || '#111827'}
                            onChange={(e) => form.setValue('personalization.text.heading', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.textHeading')}
                          />
                          <input
                            id="textHeading"
                            type="text"
                            value={form.watch('personalization.text.heading') || ''}
                            onChange={(e) => form.setValue('personalization.text.heading', e.target.value)}
                            placeholder="#111827"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Badges */}
              <div
                className="overflow-hidden"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('badges')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.badges ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.badges) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.badges) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.badges')}
                    </h4>
                    {!expandedSections.badges && (
                      <div className="flex flex-wrap items-center gap-2 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.badge.background') || '#F3E8FF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.background')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.badge.text') || '#6B21A8',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.preview.text')}
                          </span>
                        </div>
                        {(form.watch('personalization.badge.borderRadius') || 'rounded-full') && (
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {form.watch('personalization.badge.borderRadius') || 'rounded-full'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {expandedSections.badges ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.badges && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="badgeBackground" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.badgeBackground')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="badgeBackgroundColor"
                            type="color"
                            value={form.watch('personalization.badge.background') || '#F3E8FF'}
                            onChange={(e) => form.setValue('personalization.badge.background', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.badgeBackground')}
                          />
                          <input
                            id="badgeBackground"
                            type="text"
                            value={form.watch('personalization.badge.background') || ''}
                            onChange={(e) => form.setValue('personalization.badge.background', e.target.value)}
                            placeholder="#F3E8FF"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="badgeText" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.badgeText')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="badgeTextColor"
                            type="color"
                            value={form.watch('personalization.badge.text') || '#6B21A8'}
                            onChange={(e) => form.setValue('personalization.badge.text', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.badgeText')}
                          />
                          <input
                            id="badgeText"
                            type="text"
                            value={form.watch('personalization.badge.text') || ''}
                            onChange={(e) => form.setValue('personalization.badge.text', e.target.value)}
                            placeholder="#6B21A8"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="badgeBorderRadius" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.badgeBorderRadius')}
                        </label>
                        <select
                          id="badgeBorderRadius"
                          value={form.watch('personalization.badge.borderRadius') || ''}
                          onChange={(e) => form.setValue('personalization.badge.borderRadius', e.target.value)}
                          className="w-full min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                          style={{
                            color: theme.colors.foreground || '#111827',
                            backgroundColor: theme.colors.background || '#FFFFFF',
                            borderColor: theme.colors.border || '#D1D5DB',
                          }}
                        >
                          <option value="">{'rounded-full (défaut)'}</option>
                          <option value="rounded-sm">rounded-sm</option>
                          <option value="rounded-md">rounded-md</option>
                          <option value="rounded-lg">rounded-lg</option>
                          <option value="rounded-xl">rounded-xl</option>
                          <option value="rounded-2xl">rounded-2xl</option>
                          <option value="rounded-3xl">rounded-3xl</option>
                          <option value="rounded-full">rounded-full</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Icônes */}
              <div
                className="rounded-b-lg overflow-hidden"
                style={{
                  minWidth: 0,
                  backgroundColor: theme.colors.background || '#FFFFFF',
                  borderColor: theme.colors.border || '#E5E7EB',
                  border: `1px solid ${theme.colors.border || '#E5E7EB'}`,
                  borderTop: 'none',
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleSection('icons')}
                  className="w-full flex items-center justify-between p-3 transition-colors"
                  style={{
                    backgroundColor: expandedSections.icons ? (theme.colors.backgroundTertiary || '#F3F4F6') : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!expandedSections.icons) {
                      e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedSections.icons) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 text-left">
                    <h4 className="text-sm font-semibold" style={{ color: theme.colors.foreground || '#111827' }}>
                      {t('admin.configuration.personalization.editor.sections.icons')}
                    </h4>
                    {!expandedSections.icons && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-1 justify-start">
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.icon.primary') || '#9333EA',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.fields.iconPrimary')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-3 h-3 rounded border flex-shrink-0"
                            style={{
                              backgroundColor: form.watch('personalization.icon.secondary') || '#6B7280',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                          <span className="text-xs" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }}>
                            {t('admin.configuration.personalization.editor.fields.iconSecondary')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  {expandedSections.icons ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: theme.colors.foregroundSecondary || '#6B7280' }} />
                  )}
                </button>
                {expandedSections.icons && (
                  <div className="px-4 py-4" style={{ minWidth: 0 }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ minWidth: 0 }}>
                      <div>
                        <label htmlFor="iconPrimary" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.iconPrimary')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="iconPrimaryColor"
                            type="color"
                            value={form.watch('personalization.icon.primary') || '#9333EA'}
                            onChange={(e) => form.setValue('personalization.icon.primary', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.iconPrimary')}
                          />
                          <input
                            id="iconPrimary"
                            type="text"
                            value={form.watch('personalization.icon.primary') || ''}
                            onChange={(e) => form.setValue('personalization.icon.primary', e.target.value)}
                            placeholder="#9333EA"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="iconSecondary" className="block text-xs text-foreground-secondary mb-1">
                          {t('admin.configuration.personalization.editor.fields.iconSecondary')}
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="iconSecondaryColor"
                            type="color"
                            value={form.watch('personalization.icon.secondary') || '#6B7280'}
                            onChange={(e) => form.setValue('personalization.icon.secondary', e.target.value)}
                            className="personalization-color-picker"
                            aria-label={t('admin.configuration.personalization.editor.fields.iconSecondary')}
                          />
                          <input
                            id="iconSecondary"
                            type="text"
                            value={form.watch('personalization.icon.secondary') || ''}
                            onChange={(e) => form.setValue('personalization.icon.secondary', e.target.value)}
                            placeholder="#6B7280"
                            className="flex-1 min-w-0 px-3 py-2 border border-border rounded-md text-sm"
                            style={{
                              color: theme.colors.foreground || '#111827',
                              backgroundColor: theme.colors.background || '#FFFFFF',
                              borderColor: theme.colors.border || '#D1D5DB',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer fixe avec boutons */}
      <div
        className="flex gap-2 p-3 border-t flex-shrink-0"
        style={{
          backgroundColor: theme.colors.background || '#FFFFFF',
          borderTopColor: theme.colors.border || '#E5E7EB',
        }}
      >
        <button
          onClick={handleReset}
          className="px-4 py-2 text-sm rounded-lg transition-colors"
          style={{
            color: theme.colors.foregroundSecondary || '#6B7280',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.backgroundTertiary || '#F3F4F6';
            e.currentTarget.style.color = theme.colors.foreground || '#111827';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = theme.colors.foregroundSecondary || '#6B7280';
          }}
        >
          {t('admin.configuration.personalization.editor.actions.reset')}
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="ml-auto px-4 sm:px-6 py-2 text-xs sm:text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          style={{
            backgroundColor: theme.colors.primary || '#9333EA',
            color: '#FFFFFF',
          }}
          onMouseEnter={(e) => {
            if (!isSaving) {
              e.currentTarget.style.backgroundColor = '#7E22CE';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSaving) {
              e.currentTarget.style.backgroundColor = theme.colors.primary || '#9333EA';
            }
          }}
        >
          {isSaving ? t('admin.configuration.personalization.editor.actions.saving') : t('admin.configuration.personalization.editor.actions.save')}
        </button>
      </div>
    </div>
  );
};

export default PersonalizationEditor;

